import React, { useEffect, useState } from "react";
import {
    FaArrowRight,
    FaChevronLeft,
    FaChevronRight,
    FaCheckCircle,
    FaChalkboardTeacher,
    FaCompass,
    FaGraduationCap,
    FaHandshake,
    FaLightbulb,
    FaPlay,
    FaUsers,
    FaUserTie,
} from "react-icons/fa";

import api from "../api/api";
import "./HomeHeaderSave.css";

const HomeHeader = () => {
    const [slides, setSlides] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchSlides = async () => {
            try {
                const response = await api.get("/api/slides/");
                const data = response.data.results || response.data;

                setSlides(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Failed to load slides:", err);
                setError("Unable to load leadership training content.");
            } finally {
                setLoading(false);
            }
        };

        fetchSlides();
    }, []);

    useEffect(() => {
        if (slides.length <= 1) return;

        const timer = setInterval(() => {
            setCurrentIndex(
                (previous) => (previous + 1) % slides.length
            );
        }, 10000);

        return () => clearInterval(timer);
    }, [slides]);

    useEffect(() => {
        setIsVisible(false);

        const timeout = setTimeout(() => {
            setIsVisible(true);
        }, 120);

        return () => clearTimeout(timeout);
    }, [currentIndex]);

    const goToNext = () => {
        if (!slides.length) return;

        setCurrentIndex(
            (previous) => (previous + 1) % slides.length
        );
    };

    const goToPrevious = () => {
        if (!slides.length) return;

        setCurrentIndex(
            (previous) =>
                (previous - 1 + slides.length) % slides.length
        );
    };

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    if (loading) {
        return (
            <section className="cayep-loader">
                <div className="cayep-loader-content">
                    <div className="cayep-loader-mark">
                        C
                    </div>

                    <div className="cayep-loader-line">
                        <span></span>
                    </div>

                    <h3>CAYEP</h3>
                    <p>Leadership Training</p>

                    <div className="cayep-loader-spinner"></div>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="cayep-loader cayep-loader-error">
                <div className="cayep-loader-content">
                    <div className="cayep-error-mark">
                        !
                    </div>

                    <h3>Something went wrong</h3>

                    <p>{error}</p>

                    <button
                        type="button"
                        className="cayep-retry-button"
                        onClick={() => window.location.reload()}
                    >
                        Try Again
                    </button>
                </div>
            </section>
        );
    }

    if (slides.length === 0) {
        return (
            <section className="cayep-loader">
                <div className="cayep-loader-content">
                    <div className="cayep-loader-mark">
                        C
                    </div>

                    <h3>CAYEP</h3>

                    <p>
                        Empowering people to learn, lead and create
                        lasting impact.
                    </p>
                </div>
            </section>
        );
    }

    return (
        <header className="cayep-header">
            <div className="cayep-background-shape shape-one"></div>
            <div className="cayep-background-shape shape-two"></div>
            <div className="cayep-background-shape shape-three"></div>

            <div className="cayep-slider">
                {slides.map((slide, index) => (
                    <article
                        key={slide.id || index}
                        className={`cayep-slide ${
                            currentIndex === index ? "active" : ""
                        }`}
                        style={{
                            backgroundImage: `
                                linear-gradient(
                                    105deg,
                                    rgba(6, 42, 75, 0.96) 0%,
                                    rgba(6, 42, 75, 0.88) 35%,
                                    rgba(6, 42, 75, 0.57) 64%,
                                    rgba(6, 42, 75, 0.28) 100%
                                ),
                                url("${slide.image}")
                            `,
                        }}
                    >
                        <div className="cayep-image-wash"></div>

                        {currentIndex === index && (
                            <div
                                className={`cayep-content ${
                                    isVisible ? "animate" : ""
                                }`}
                            >
                                <div className="cayep-top-brand">
                                    <div className="cayep-brand-mark">
                                        C
                                    </div>

                                    <div className="cayep-brand-text">
                                        <strong>CAYEP</strong>
                                        <span>
                                            LEADERSHIP TRAINING
                                        </span>
                                    </div>
                                </div>

                                <div className="cayep-kicker">
                                    <span></span>
                                    LEADERSHIP • CAPACITY • DEVELOPMENT
                                </div>

                                <h1>
                                    {slide.title}
                                </h1>

                                <div className="cayep-heading-line">
                                    <span></span>
                                    <b>01</b>
                                </div>

                                <p className="cayep-description">
                                    {slide.description}
                                </p>

                                <div className="cayep-actions">
                                    <a
                                        href="#programs"
                                        className="cayep-primary-button"
                                    >
                                        Explore Training
                                        <FaArrowRight />
                                    </a>

                                    <button
                                        type="button"
                                        className="cayep-secondary-button"
                                    >
                                        <span className="cayep-play-icon">
                                            <FaPlay />
                                        </span>

                                        Meet CAYEP
                                    </button>
                                </div>

                                <div className="cayep-values">
                                    <div>
                                        <FaCheckCircle />
                                        <span>
                                            Leadership Development
                                        </span>
                                    </div>

                                    <div>
                                        <FaCheckCircle />
                                        <span>
                                            Professional Growth
                                        </span>
                                    </div>

                                    <div>
                                        <FaCheckCircle />
                                        <span>
                                            Community Impact
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </article>
                ))}

                <div className="cayep-slider-controls">
                    <button
                        type="button"
                        className="cayep-arrow"
                        onClick={goToPrevious}
                        aria-label="Previous slide"
                    >
                        <FaChevronLeft />
                    </button>

                    <button
                        type="button"
                        className="cayep-arrow"
                        onClick={goToNext}
                        aria-label="Next slide"
                    >
                        <FaChevronRight />
                    </button>
                </div>

                <div className="cayep-slide-number">
                    <strong>
                        {String(currentIndex + 1).padStart(2, "0")}
                    </strong>

                    <span></span>

                    <small>
                        {String(slides.length).padStart(2, "0")}
                    </small>
                </div>
            </div>

            <div className="leadership-journey">

                <div className="journey-heading">
                    <span>THE CAYEP JOURNEY</span>
                    <small>
                        Every step of learning moves you forward.
                    </small>
                </div>

                <div className="journey-path"></div>

                <div className="journey-step step-learn">
                    <span className="step-number">01</span>
                    <div className="step-icon">
                        <FaGraduationCap />
                    </div>
                    <strong>LEARN</strong>
                    <small>Knowledge</small>
                </div>

                <div className="journey-step step-grow">
                    <span className="step-number">02</span>
                    <div className="step-icon">
                        <FaLightbulb />
                    </div>
                    <strong>GROW</strong>
                    <small>Capacity</small>
                </div>

                <div className="journey-step step-lead">
                    <span className="step-number">03</span>
                    <div className="step-icon">
                        <FaUserTie />
                    </div>
                    <strong>LEAD</strong>
                    <small>Purpose</small>
                </div>

                <div className="journey-step step-impact">
                    <span className="step-number">04</span>
                    <div className="step-icon">
                        <FaHandshake />
                    </div>
                    <strong>IMPACT</strong>
                    <small>Community</small>
                </div>

                <div className="journey-leader">

                    <div className="leader-shadow"></div>

                    <div className="leader-body">
                        <div className="leader-head"></div>

                        <div className="leader-neck"></div>

                        <div className="leader-torso">
                            <div className="leader-book">
                                <FaGraduationCap />
                            </div>
                        </div>

                        <div className="leader-arm leader-arm-back"></div>
                        <div className="leader-arm leader-arm-front"></div>

                        <div className="leader-leg leader-leg-back"></div>
                        <div className="leader-leg leader-leg-front"></div>

                        <div className="leader-foot leader-foot-back"></div>
                        <div className="leader-foot leader-foot-front"></div>
                    </div>

                    <div className="leader-trail"></div>
                </div>

                <div className="journey-caption">
                    <FaCompass />

                    <span>
                        LEARN TODAY
                        <b>LEAD TOMORROW</b>
                    </span>
                </div>
            </div>

            <aside className="cayep-side-rail">

                <div className="rail-top">
                    <span>01</span>
                    <div></div>
                    <span>
                        CAYEP
                    </span>
                </div>

                <div className="rail-content">

                    <span className="rail-label">
                        OUR PURPOSE
                    </span>

                    <h2>
                        Developing
                        <strong>
                            people who lead.
                        </strong>
                    </h2>

                    <p>
                        We create learning experiences that
                        strengthen leadership, build confidence,
                        and prepare people to make meaningful
                        contributions in their communities.
                    </p>

                    <a
                        href="#about"
                        className="rail-link"
                    >
                        Discover CAYEP
                        <FaArrowRight />
                    </a>
                </div>

                <div className="rail-bottom">

                    <div className="rail-stat">
                        <FaUsers />
                        <span>
                            PEOPLE
                            <b>FIRST</b>
                        </span>
                    </div>

                    <div className="rail-stat">
                        <FaChalkboardTeacher />
                        <span>
                            LEARN
                            <b>TO LEAD</b>
                        </span>
                    </div>
                </div>
            </aside>

            {slides.length > 1 && (
                <div className="cayep-indicators">
                    {slides.map((_, index) => (
                        <button
                            type="button"
                            key={index}
                            className={`cayep-indicator ${
                                currentIndex === index
                                    ? "active"
                                    : ""
                            }`}
                            onClick={() => goToSlide(index)}
                            aria-label={`Go to slide ${
                                index + 1
                            }`}
                        >
                            <span></span>
                        </button>
                    ))}
                </div>
            )}

            <div className="cayep-bottom-bar">
                <div className="bottom-brand">
                    <strong>CAYEP</strong>
                    <span>
                        LEADERSHIP TRAINING
                    </span>
                </div>

                <div className="bottom-message">
                    <span></span>
                    DEVELOPING LEADERS
                    <b>•</b>
                    BUILDING COMMUNITIES
                    <b>•</b>
                    CREATING IMPACT
                </div>

                <div className="bottom-mark">
                    <span>LEARN</span>
                    <i>→</i>
                    <span>LEAD</span>
                </div>
            </div>
        </header>
    );
};

export default HomeHeader;